import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { green, red, amber, blue } from '@mui/material/colors';
import { ApiInstance } from '../../../api/api';
import {
  DialogState,
  BaseDhcpReservationCreateRequestType,
  DhcpReservationUpdateResponse,
} from '../../../types/types';
import IconButton from '@mui/material/IconButton';
import UpdateDhcpReservationForm from './forms/UpdateDhcpReservationForm';
import DialogContentLoading from '../common/DialogContentLoading';
import DialogContentSuccess from '../common/DialogContentSuccess';
import DialogContentError from '../common/DialogContentError';
import dialogStyles from '../common/dialogStyles';
import { parseApiError } from '../../../utils/apiErrorHandler';
import useApplyFilterParam from '../../../hooks/useApplyFilterParam';

interface UpdateDhcpReservationDialogProps {
  open: boolean;
  onClose: () => void;
  ipAddress: string | undefined;
  deviceType: string | null;
  region: string | null;
  buildingCode: string | null;
  unit: string;
  unitName: string | null;
  dhcpInterface: string;
  dhcpReservationIp: string;
  dhcpReservationMac: string;
  dhcpReservationDescription: string;
}

function useSelectedItem(currentDescription: string) {
  const [newDescription, setNewDescription] = useState<string>('');
  const [touched, setTouched] = useState<{
    newDescription: boolean;
  }>({
    newDescription: false,
  });
  const allowedInputCharacters: RegExp = /^[a-zA-Z0-9-_]*$/;
  const isDescriptionUnique: boolean = newDescription.toLowerCase() !== currentDescription;
  const isDescriptionValid: boolean =
    newDescription.trim().length > 0 &&
    allowedInputCharacters.test(newDescription) &&
    isDescriptionUnique;
  const markDescriptionTouched = useCallback(
    () => setTouched((t) => ({ ...t, newDescription: true })),
    []
  );

  return {
    newDescription,
    setNewDescription,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markDescriptionTouched,
    setTouched,
  };
}

const UpdateDhcpReservationDialog: React.FC<UpdateDhcpReservationDialogProps> = ({
  open,
  onClose,
  ipAddress,
  deviceType,
  region,
  buildingCode,
  unit,
  unitName,
  dhcpInterface,
  dhcpReservationIp,
  dhcpReservationMac,
  dhcpReservationDescription,
}) => {
  const {
    newDescription,
    setNewDescription,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markDescriptionTouched,
    setTouched,
  } = useSelectedItem(dhcpReservationDescription);
  const isConfirmDisabled = !isDescriptionValid;
  const descriptionRef = useRef<HTMLInputElement | null>(null);

  const resetForm = () => {
    setNewDescription('');
    setTouched({ newDescription: false });
  };

  const DEFAULT_LOADING_TEXT = 'Please wait while we process your request...';
  const DEFAULT_ERROR_TEXT = 'Something went wrong. Please try again later.';

  const titleMap: Record<DialogState, string> = {
    default: 'Update DHCP Reservation',
    loading: 'Update DHCP Reservation',
    success: 'Update DHCP Reservation',
    error: 'Update DHCP Reservation',
  };

  const backgroundColorMap: Record<DialogState, string> = {
    default: amber[800],
    loading: blue[600],
    success: green[500],
    error: red[500],
  };

  const [dialogState, setDialogState] = useState<DialogState>('default');
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [errorText, setErrorText] = useState(DEFAULT_ERROR_TEXT);
  const [reloadOnClose, setReloadOnClose] = useState<Boolean>(false);
  const [actionResult, setActionResult] = React.useState<DhcpReservationUpdateResponse | null>(
    null
  );

  const applyFilterAfterUpdate = useApplyFilterParam('mac_address', actionResult?.data.mac ?? '');

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDialogState('default');
        setLoadingText(DEFAULT_LOADING_TEXT);
        setErrorText(DEFAULT_ERROR_TEXT);
        setReloadOnClose(false);
      }, 100);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!isConfirmDisabled) {
      setDialogState('loading');
      setLoadingText(DEFAULT_LOADING_TEXT);

      try {
        const payload: BaseDhcpReservationCreateRequestType = {
          device_ip: ipAddress,
          device_vendor: deviceType,
          region: region,
          building_code: buildingCode,
          unit: unit,
          unit_name: unitName,
          reservation_data: {
            interface: dhcpInterface,
            dhcp_reservation_ip: dhcpReservationIp,
            dhcp_reservation_mac: dhcpReservationMac,
            dhcp_reservation_description: dhcpReservationDescription,
            new_dhcp_reservation_description: newDescription.toLowerCase(),
          },
        };

        const response = await ApiInstance.updateDhcpReservation(payload);

        setDialogState('success');
        setActionResult(response);
        setReloadOnClose(true);
      } catch (error: any) {
        setDialogState('error');
        setReloadOnClose(false);

        setErrorText(parseApiError(error));
      }
    } else {
      if (!isDescriptionValid && descriptionRef.current) {
        descriptionRef.current.focus();
      }
    }
  };

  const handleClose = () => {
    if (reloadOnClose) {
      resetForm();
      applyFilterAfterUpdate();
    } else {
      resetForm();
      onClose();
    }
  };

  const getDialogTitle = () => titleMap[dialogState];
  const getTitleBackgroundColor = () => backgroundColorMap[dialogState];

  const getDialogContent = () => {
    switch (dialogState) {
      case 'loading':
        return <DialogContentLoading loadingText={loadingText} />;
      case 'success':
        return <DialogContentSuccess waitBlock={false} />;
      case 'error':
        return <DialogContentError errorText={errorText} />;
      default:
        return (
          <>
            <UpdateDhcpReservationForm
              reservationIp={dhcpReservationIp}
              reservationMac={dhcpReservationMac}
              reservationDescription={dhcpReservationDescription}
              newDescription={newDescription}
              setNewDescription={setNewDescription}
              allowedInputCharacters={allowedInputCharacters}
              isDescriptionValid={isDescriptionValid}
              touched={touched}
              markDescriptionTouched={markDescriptionTouched}
            />
          </>
        );
    }
  };

  const showCloseIcon = dialogState !== 'loading';
  const showDialogActions = dialogState === 'default';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogStyles.dialog}
      sx={dialogStyles.dialogPaper}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          ...dialogStyles.dialogTitle,
          backgroundColor: getTitleBackgroundColor(),
        }}
      >
        {getDialogTitle()}
        {showCloseIcon && (
          <IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent id="dialog-content" sx={dialogStyles.dialogContent}>
        {getDialogContent()}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UpdateDhcpReservationDialog;
